import apiClient from './apiClient';

// Bank types
export interface BankResponse {
    bankId: string;
    bankName: string;
    bankCode: string;
    contactEmail: string;
    contactPhone: string;
    websiteUrl: string;
    isActive: boolean;
    acceptsApplications: boolean;
    averageResponseDays: number;
    averageApprovalRate: number;
    createdAt: string;
    logoFilename: string | null;
    logoAltText: string;
    logoUrl: string;
    primaryColor: string | null;
    secondaryColor: string | null;
}

// Public bank types (for landing page)
export interface PublicBankInfo {
    bankId: string;
    bankCode: string;
    bankName: string;
    logoUrl: string;
    websiteUrl: string;
    countryCode: string;
    isActive: boolean;
    acceptsApplications: boolean;
}

export interface PublicBanksResponse {
    success: boolean;
    countryCode: string;
    count: number;
    banks: PublicBankInfo[];
    logoBaseUrl: string;
    defaultLogoUrl: string;
}

export interface CreateBankRequest {
    name: string;
    code: string;
    min_credit_score: number;
    max_ltv: number;
    interest_rate: number;
    processing_time: string;
}

export interface UpdateBankRequest {
    name?: string;
    code?: string;
    min_credit_score?: number;
    max_ltv?: number;
    interest_rate?: number;
    processing_time?: string;
}

export interface BankStatistics {
    totalBanks: number;
    activeBanks: number;
    inactiveBanks: number;
    totalApplications: number;
    averageApprovalRate: number;
}

export interface DistributionStatusResponse {
    banks_contacted: number;
    responses_received: number;
    distribution_status: string;
    bank_responses: Array<{
        bank_name: string;
        response_type: string;
        message: string;
        responded_at: string;
    }>;
}

// Email Routing Types
export interface BankEmailRoutingResponse {
    bankId: string;
    bankName: string;
    bankCode: string;
    isActive: boolean;
    acceptsApplications: boolean;
    isRegisteredInRegistry: boolean;
    internalEmailAddress: string | null;
    preferredLanguage: string | null;
    cloudflareStatus: string;
    isCloudflareConfigured: boolean;
    cloudflareDestination: string | null;
    cloudflareError: string | null;
    setupInstructions: string | null;
}

export interface EmailRoutingSummary {
    totalBanks: number;
    registeredInRegistry: number;
    cloudflareConfigured: number;
    pendingCloudflareSetup: number;
    notRegistered: number;
}

export interface EmailRoutingStatusResponse {
    summary: EmailRoutingSummary;
    routingDomain: string;
    cloudflareEnabled: boolean;
    banks: BankEmailRoutingResponse[];
}

class BankService {
    /**
     * List all banks with filtering
     * GET /admin/banks
     */
    async getBanks(params?: { status?: string; search?: string }): Promise<BankResponse[]> {
        const response = await apiClient.get('/admin/banks', { params });
        // Handle nested response structure
        if (response.data.data) {
            if (Array.isArray(response.data.data)) {
                return response.data.data;
            }
            if (response.data.data.banks && Array.isArray(response.data.data.banks)) {
                return response.data.data.banks;
            }
        }
        return response.data;
    }

    /**
     * Get bank by ID
     * GET /admin/banks/{bankId}
     */
    async getBank(bankId: string): Promise<BankResponse> {
        const response = await apiClient.get(`/admin/banks/${bankId}`);
        return response.data.data || response.data;
    }

    /**
     * Create new bank
     * POST /admin/banks
     */
    async createBank(request: CreateBankRequest): Promise<BankResponse> {
        const response = await apiClient.post('/admin/banks', request);
        return response.data.data || response.data;
    }

    /**
     * Update bank
     * PUT /admin/banks/{bankId}
     */
    async updateBank(bankId: string, request: UpdateBankRequest): Promise<BankResponse> {
        const response = await apiClient.put(`/admin/banks/${bankId}`, request);
        return response.data.data || response.data;
    }

    /**
     * Soft delete bank
     * DELETE /admin/banks/{bankId}
     */
    async deleteBank(bankId: string): Promise<void> {
        await apiClient.delete(`/admin/banks/${bankId}`);
    }

    /**
     * Activate bank
     * PUT /admin/banks/{bankId}/activate
     */
    async activateBank(bankId: string): Promise<BankResponse> {
        const response = await apiClient.put(`/admin/banks/${bankId}/activate`);
        return response.data.data || response.data;
    }

    /**
     * Deactivate bank
     * PUT /admin/banks/{bankId}/deactivate
     */
    async deactivateBank(bankId: string): Promise<BankResponse> {
        const response = await apiClient.put(`/admin/banks/${bankId}/deactivate`);
        return response.data.data || response.data;
    }

    /**
     * Get bank statistics
     * GET /admin/banks/statistics
     */
    async getBankStatistics(): Promise<BankStatistics> {
        const response = await apiClient.get('/admin/banks/statistics');
        return response.data.data || response.data;
    }

    /**
     * Get distribution status for an application
     * GET /bank/applications/{applicationId}/status
     */
    async getDistributionStatus(applicationId: string): Promise<DistributionStatusResponse> {
        const response = await apiClient.get(`/bank/applications/${applicationId}/status`);
        return response.data.data || response.data;
    }

    /**
     * Get banks by country (public endpoint)
     * GET /banks/country/{countryCode}
     */
    async getBanksByCountry(countryCode: string): Promise<PublicBanksResponse> {
        const response = await apiClient.get(`/banks/country/${countryCode}`);
        return response.data;
    }

    /**
     * Get carousel-visible banks by country (public endpoint)
     * GET /banks/country/{countryCode}/carousel
     * 
     * Only returns banks that have carouselVisible = true.
     * Use for the "Trusted by leading banks" section.
     * 
     * @param countryCode ISO country code (e.g., "DO")
     * @param demoSecret Optional demo secret — when provided, backend returns
     *                   ALL banks with logos regardless of carousel visibility.
     *                   Triggered by ?secret= in the page URL.
     */
    async getCarouselBanksByCountry(countryCode: string, demoSecret?: string): Promise<PublicBanksResponse> {
        const params: Record<string, string> = {};
        if (demoSecret) {
            params.demo = demoSecret;
        }
        const response = await apiClient.get(`/banks/country/${countryCode}/carousel`, { params });
        return response.data;
    }

    // ==================== EMAIL ROUTING ====================

    /**
     * Get email routing status for all banks
     * GET /admin/banks/email-routing
     */
    async getEmailRoutingStatus(): Promise<EmailRoutingStatusResponse> {
        const response = await apiClient.get('/admin/banks/email-routing');
        return response.data.data || response.data;
    }

    /**
     * Get email routing status for a specific bank
     * GET /admin/banks/email-routing/{bankId}
     */
    async getEmailRoutingStatusForBank(bankId: string): Promise<BankEmailRoutingResponse> {
        const response = await apiClient.get(`/admin/banks/email-routing/${bankId}`);
        return response.data.data || response.data;
    }
}

export const bankService = new BankService();
export default bankService;